<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import { FUNNEL_TEMPLATES } from 'dashboard/constants/funnelTemplates';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { funnels, loadFunnels, createFunnel, deleteFunnel } = useFunnelManager();

// Estado local
const showCreateModal = ref(false);
const selectedTemplate = ref(null);
const isLoading = ref(true);
const loading = ref({});

// Estado do funil customizado
const customFunnel = ref({
  name: '',
  description: '',
  stages: [],
});

const newStageName = ref('');
const newStageColor = ref('#3B82F6');
const newStageDescription = ref('');
const editingStageIndex = ref(null);

// Templates de funil
const funnelTemplates = FUNNEL_TEMPLATES.map(template => ({
  type: template.id,
  name: template.name,
  icon: template.settings.icon,
  description: template.description,
  stages: template.stages,
}));

// Cores para estágios
const stageColors = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
];

// Table headers
const tableHeaders = computed(() => [
  t('CRM.FUNNELS.TABLE.NAME'),
  t('CRM.FUNNELS.TABLE.STAGES'),
  t('CRM.FUNNELS.TABLE.TYPE'),
]);

// Methods
const resetForm = () => {
  customFunnel.value = { name: '', description: '', stages: [] };
  newStageName.value = '';
  newStageDescription.value = '';
  newStageColor.value = '#3B82F6';
  editingStageIndex.value = null;
  selectedTemplate.value = null;
};

const addStage = () => {
  if (!newStageName.value.trim()) return;

  const stage = {
    label: newStageName.value.trim(),
    color: newStageColor.value,
    description: newStageDescription.value.trim(),
    position: customFunnel.value.stages.length,
  };

  if (editingStageIndex.value !== null) {
    customFunnel.value.stages[editingStageIndex.value] = stage;
    editingStageIndex.value = null;
  } else {
    customFunnel.value.stages.push(stage);
  }

  newStageName.value = '';
  newStageDescription.value = '';
  newStageColor.value =
    stageColors[customFunnel.value.stages.length % stageColors.length];
};

const editStage = index => {
  const stage = customFunnel.value.stages[index];
  newStageName.value = stage.label;
  newStageColor.value = stage.color;
  newStageDescription.value = stage.description || '';
  editingStageIndex.value = index;
};

const removeStage = index => {
  customFunnel.value.stages.splice(index, 1);
  customFunnel.value.stages.forEach((stage, i) => {
    stage.position = i;
  });
};

// Extrai a mensagem REAL do backend (o toast genérico escondia a causa do erro).
const backendError = error => {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data.error === 'string') return data.error;
  const errs = data.errors;
  if (Array.isArray(errs)) return errs.join(', ');
  if (errs && typeof errs === 'object')
    return Object.values(errs).flat().join(', ');
  return null;
};

const handleCreateFunnel = async () => {
  if (selectedTemplate.value) {
    // Os stages do template (front) já estão no formato do backend (objeto com
    // label+position), então cria DIRETO — sem chamar o from_template, que quebrava
    // por mismatch das ids PT (vendas/suporte…) x chaves EN (sales/support…).
    const tpl = selectedTemplate.value;
    const funnelData = {
      name: customFunnel.value.name || tpl.name,
      description: customFunnel.value.description || tpl.description,
      funnel_type: 'custom',
      stages: tpl.stages,
      settings: {
        agent_ids: [],
        team_ids: [],
        auto_assign: false,
        default_stage: Object.keys(tpl.stages || {})[0],
      },
    };
    try {
      await createFunnel({ funnel: funnelData });
      useAlert(t('CRM.FUNNELS.ADD.API.SUCCESS_MESSAGE'));
      showCreateModal.value = false;
      resetForm();
      await loadFunnels();
    } catch (error) {
      useAlert(backendError(error) || t('CRM.FUNNELS.ADD.API.ERROR_MESSAGE'));
    }
    return;
  }

  if (!customFunnel.value.name) {
    useAlert(t('CRM.FUNNELS.ADD.FORM.NAME_REQUIRED'));
    return;
  }

  if (customFunnel.value.stages.length === 0) {
    useAlert(t('CRM.FUNNELS.ADD.FORM.STAGES_REQUIRED'));
    return;
  }

  const stagesObj = {};
  customFunnel.value.stages.forEach((stage, index) => {
    const key = stage.label.toLowerCase().replace(/\s+/g, '_');
    stagesObj[key] = {
      ...stage,
      position: index,
      allow_new_conversations: index === 0,
    };
  });

  const funnelData = {
    name: customFunnel.value.name,
    description: customFunnel.value.description,
    funnel_type: 'custom',
    stages: stagesObj,
    settings: {
      agent_ids: [],
      team_ids: [],
      auto_assign: false,
      default_stage: Object.keys(stagesObj)[0],
    },
  };

  try {
    await createFunnel({ funnel: funnelData });
    useAlert(t('CRM.FUNNELS.ADD.API.SUCCESS_MESSAGE'));
    showCreateModal.value = false;
    resetForm();
    await loadFunnels();
  } catch (error) {
    useAlert(backendError(error) || t('CRM.FUNNELS.ADD.API.ERROR_MESSAGE'));
  }
};

const selectTemplate = template => {
  if (selectedTemplate.value?.type === template.type) {
    // Deseleciona se clicar no mesmo
    selectedTemplate.value = null;
    customFunnel.value.name = '';
    customFunnel.value.description = '';
  } else {
    selectedTemplate.value = template;
    customFunnel.value.name = template.name;
    customFunnel.value.description = template.description;
  }
};

const openCreateModal = () => {
  resetForm();
  showCreateModal.value = true;
};

const goToFunnelSettings = funnel => {
  const accountId = store.getters.getCurrentAccountId;
  router.push({
    name: 'funnel_settings',
    params: { accountId, funnelId: funnel.id },
  });
};

const handleDeleteFunnel = async funnel => {
  loading.value[funnel.id] = true;
  try {
    await deleteFunnel(funnel.id);
    useAlert(t('CRM.FUNNELS.DELETE.API.SUCCESS_MESSAGE'));
    await loadFunnels();
  } catch (error) {
    useAlert(t('CRM.FUNNELS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[funnel.id] = false;
  }
};

// Lifecycle
onMounted(async () => {
  await loadFunnels();
  isLoading.value = false;
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('CRM.FUNNELS.LOADING')"
    :no-records-found="!funnels.length"
    :no-records-message="$t('CRM.FUNNELS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CRM.FUNNELS.HEADER')"
        :description="$t('CRM.FUNNELS.DESCRIPTION')"
        feature-name="funnels"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CRM.FUNNELS.HEADER_BTN_TXT')"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="funnel in funnels" :key="funnel.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ funnel.name }}
                </span>
              </div>
              <p
                v-if="funnel.description"
                class="mt-1 text-sm text-n-slate-11 line-clamp-1"
              >
                {{ funnel.description }}
              </p>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="(stage, key) in funnel.stages"
                  :key="key"
                  class="px-2 py-0.5 text-xs font-medium rounded-full"
                  :style="{
                    backgroundColor: stage.color + '20',
                    color: stage.color,
                  }"
                >
                  {{ stage.label }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="px-2 py-1 text-xs font-medium rounded bg-n-alpha-2 text-n-slate-11"
              >
                {{ funnel.funnel_type || 'custom' }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('CRM.FUNNELS.FORM.SETTINGS')"
                  icon="i-lucide-settings"
                  slate
                  xs
                  faded
                  @click="goToFunnelSettings(funnel)"
                />
                <Button
                  v-tooltip.top="$t('CRM.FUNNELS.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[funnel.id]"
                  @click="handleDeleteFunnel(funnel)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- Modal de Criação -->
    <woot-modal
      v-model:show="showCreateModal"
      :on-close="
        () => {
          showCreateModal = false;
          resetForm();
        }
      "
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          :header-title="$t('CRM.FUNNELS.ADD.TITLE')"
          :header-content="$t('CRM.FUNNELS.ADD.DESCRIPTION')"
        />
        <form class="flex flex-wrap mx-0" @submit.prevent="handleCreateFunnel">
          <woot-input
            v-model="customFunnel.name"
            class="w-full"
            :label="$t('CRM.FUNNELS.ADD.FORM.NAME.LABEL')"
            :placeholder="$t('CRM.FUNNELS.ADD.FORM.NAME.PLACEHOLDER')"
          />
          <woot-input
            v-model="customFunnel.description"
            class="w-full"
            :label="$t('CRM.FUNNELS.ADD.FORM.DESCRIPTION.LABEL')"
            :placeholder="$t('CRM.FUNNELS.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
          />

          <!-- Templates -->
          <div class="w-full">
            <label class="block mb-3 text-sm font-medium text-n-slate-12">
              {{ $t('CRM.FUNNELS.ADD.FORM.TEMPLATE.LABEL') }}
            </label>
            <div class="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1">
              <button
                v-for="template in funnelTemplates"
                :key="template.type"
                type="button"
                class="relative bg-n-solid-1 cursor-pointer rounded-xl flex flex-col justify-start transition-all duration-200 ease-in p-4 items-start border border-solid"
                :class="[
                  selectedTemplate?.type === template.type
                    ? 'border-n-blue-9 shadow-md ring-1 ring-n-blue-9'
                    : 'border-n-weak hover:border-n-blue-9 hover:shadow-md',
                ]"
                @click="selectTemplate(template)"
              >
                <div class="flex items-center gap-2 mb-2">
                  <div
                    class="flex size-8 items-center justify-center rounded-full bg-n-alpha-2"
                  >
                    <fluent-icon
                      icon="filter"
                      size="16"
                      class="text-n-slate-10"
                    />
                  </div>
                  <h3 class="text-n-slate-12 text-sm font-medium">
                    {{ template.name }}
                  </h3>
                </div>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="(stage, key) in template.stages"
                    :key="key"
                    class="px-1.5 py-0.5 text-xs rounded-md"
                    :style="{
                      backgroundColor: stage.color + '15',
                      color: stage.color,
                    }"
                  >
                    {{ stage.label }}
                  </span>
                </div>
                <div
                  v-if="selectedTemplate?.type === template.type"
                  class="absolute top-2 right-2"
                >
                  <fluent-icon
                    icon="checkmark-circle"
                    size="16"
                    class="text-n-blue-9"
                  />
                </div>
              </button>
            </div>
          </div>

          <!-- Etapas Customizadas -->
          <div v-if="!selectedTemplate" class="w-full mt-4">
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('CRM.FUNNELS.ADD.FORM.STAGES.LABEL') }}
            </label>
            <div class="flex gap-2 mb-2">
              <woot-input
                v-model="newStageName"
                class="flex-1 mb-0"
                :placeholder="$t('CRM.FUNNELS.ADD.FORM.STAGES.PLACEHOLDER')"
                @keyup.enter.prevent="addStage"
              />
              <woot-color-picker v-model="newStageColor" />
              <Button
                icon="i-lucide-plus"
                type="button"
                slate
                faded
                @click="addStage"
              />
            </div>
            <div class="space-y-1 max-h-32 overflow-y-auto">
              <div
                v-for="(stage, index) in customFunnel.stages"
                :key="index"
                class="flex items-center gap-2 p-2 rounded bg-n-alpha-1"
              >
                <div
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="flex-1 text-sm text-n-slate-12">
                  {{ stage.label }}
                </span>
                <Button
                  icon="i-lucide-pen"
                  type="button"
                  xs
                  slate
                  faded
                  @click="editStage(index)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  type="button"
                  xs
                  ruby
                  faded
                  @click="removeStage(index)"
                />
              </div>
              <p
                v-if="customFunnel.stages.length === 0"
                class="text-sm text-center text-n-slate-11 py-4"
              >
                {{ $t('CRM.FUNNELS.ADD.FORM.STAGES.EMPTY') }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center justify-end w-full gap-2 px-0 py-2 mt-4"
          >
            <Button
              faded
              slate
              type="reset"
              :label="$t('CRM.FUNNELS.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="
                showCreateModal = false;
                resetForm();
              "
            />
            <Button
              type="submit"
              :label="$t('CRM.FUNNELS.ADD.FORM.SUBMIT')"
              :disabled="!customFunnel.name && !selectedTemplate"
            />
          </div>
        </form>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
